"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { CopyableCode } from "@/components/ui/CopyableCode";
import { signIn, signOut } from "@/lib/auth/client";
import { copyToClipboard } from "@/lib/clipboard";

type ListedKey = { id: string; label: string; createdAt: string; masked: string };
type InitialUser = { email: string; name: string | null } | null;

// `createdAt` is a full ISO timestamp and this component is server-rendered
// (/account is `force-dynamic`) before hydrating, so a bare
// toLocaleDateString() resolves the *host's* locale and time zone twice and
// gets two different strings — React hydration mismatch #418, observed
// alongside Sentry SCHOLAR-SIDEKICK-3S from an en-IN/IST browser. Pin both so
// Node and the browser always agree.
const CREATED_AT_FORMAT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

export default function AccountClient({
  initialUser,
  initialKeys,
}: {
  initialUser: InitialUser;
  initialKeys: ListedKey[];
}) {
  if (!initialUser) return <SignedOut />;
  return <SignedIn user={initialUser} initialKeys={initialKeys} />;
}

function SignedOut() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  async function requestMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    try {
      // Better Fetch returns {error} for HTTP failures, but a transport-level
      // failure (Safari "Load failed" — see Sentry SCHOLAR-SIDEKICK-3M) rejects
      // instead; without this catch the form stays stuck on "Sending…".
      const res = await signIn.magicLink({ email, callbackURL: "/account" });
      setStatus(res.error ? "error" : "sent");
    } catch {
      setStatus("error");
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Sign in</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {status === "sent" ? (
          <p role="status" className="text-sm">
            Check your inbox - we sent a sign-in link to <strong>{email}</strong>.
          </p>
        ) : (
          <form onSubmit={requestMagicLink} className="space-y-3">
            <label htmlFor="email" className="block text-sm font-medium">
              Email address
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
              placeholder="you@example.com"
            />
            <Button type="submit" disabled={status === "sending"}>
              {status === "sending" ? "Sending…" : "Email me a sign-in link"}
            </Button>
            {status === "error" && (
              <p role="alert" className="text-sm text-red-600">
                Something went wrong. Please try again.
              </p>
            )}
          </form>
        )}

        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="h-px flex-1 bg-gray-200" />
          or
          <span className="h-px flex-1 bg-gray-200" />
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              // Floating promise otherwise: a fetch aborted by the redirect
              // navigation rejects and lands on window.onunhandledrejection.
              void signIn
                .social({ provider: "google", callbackURL: "/account" })
                .catch(() => setStatus("error"));
            }}
          >
            Continue with Google
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void signIn
                .social({ provider: "github", callbackURL: "/account" })
                .catch(() => setStatus("error"));
            }}
          >
            Continue with GitHub
          </Button>
        </div>

        {/*
          Sits below every sign-in route (magic link and both social buttons) so
          it is adjacent to whichever control the user actually presses, and it
          stays visible after the form is replaced by the "check your inbox"
          message. Creating an account is the point of acceptance, so this is
          the only place the site asks for it.
        */}
        <p className="text-sm text-gray-600">
          By signing in you agree to our{" "}
          <Link className="text-brand underline hover:text-brand/80" href="/legal/terms">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link className="text-brand underline hover:text-brand/80" href="/legal/privacy">
            Privacy Policy
          </Link>
          . We will only email you about your account and the service.
        </p>
      </CardContent>
    </Card>
  );
}

function SignedIn({
  user,
  initialKeys,
}: {
  user: { email: string; name: string | null };
  initialKeys: ListedKey[];
}) {
  const router = useRouter();
  const [keys, setKeys] = useState<ListedKey[]>(initialKeys);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function createKey(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setCreated(null);
    try {
      const res = await fetch("/api/account/keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to create key");
      setCreated(data.plaintext as string);
      setKeys((prev) => [data.key as ListedKey, ...prev]);
      setLabel("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create key");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setError(null);
    try {
      // A transport-level failure (Firefox "NetworkError when attempting to
      // fetch resource." — see Sentry SCHOLAR-SIDEKICK-3S) rejects rather than
      // resolving with a non-ok response. Without this catch the click fails
      // silently — the key stays listed with no error shown — and the rejection
      // escapes to window.onunhandledrejection.
      const res = await fetch(`/api/account/keys/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to revoke key");
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch {
      setError("Failed to revoke key");
    }
  }

  async function copyPlaintext() {
    if (!created) return;
    // copyToClipboard never throws (Safari rejects writeText outside a user
    // gesture — see Sentry SCHOLAR-SIDEKICK-3J); false = give up silently, the
    // key is still visible on-screen for manual copy.
    const ok = await copyToClipboard(created);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="mt-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Signed in</CardTitle>
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              try {
                await signOut();
              } catch {
                setError("Failed to sign out");
              } finally {
                // /account is a server component that reads the session at request
                // time; refresh so it re-renders the signed-out state immediately.
                router.refresh();
              }
            }}
          >
            Sign out
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">{user.email}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>API keys</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Use a key as <code>Authorization: Bearer ssk_…</code> for higher rate limits than
            anonymous access.
          </p>

          {created && (
            <div role="status" className="rounded-md border border-green-300 bg-green-50 p-3">
              <p className="text-sm font-medium">
                Copy your new key now - it won&apos;t be shown again:
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-white px-2 py-1 text-xs">
                  {created}
                </code>
                <Button type="button" onClick={copyPlaintext}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>

              {/* Activation quickstart: the plaintext key exists only in this
                  render, so this is the one moment a paste-ready snippet can
                  carry it. Snippets must stay zsh-safe (no leading-# lines,
                  balanced single quotes) — guardrail-scanned. */}
              <div className="mt-4 space-y-3">
                <p className="text-sm font-medium">Make your first call - pick your surface:</p>
                <div>
                  <p className="mb-1 text-xs text-gray-600">Terminal (curl)</p>
                  <CopyableCode
                    ariaLabel="Copy curl example"
                    code={`curl -X POST https://scholar-sidekick.com/api/format -H 'Authorization: Bearer ${created}' -H 'Content-Type: application/json' -d '{"text":"10.1038/nphys1170","style":"vancouver"}'`}
                  />
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-600">
                    MCP client config (Claude Desktop, Cursor, Codex - hosted remote)
                  </p>
                  <CopyableCode
                    ariaLabel="Copy MCP config example"
                    code={`{
  "mcpServers": {
    "scholar-sidekick": {
      "url": "https://scholar-sidekick.com/api/mcp",
      "headers": { "Authorization": "Bearer ${created}" }
    }
  }
}`}
                  />
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-600">
                    Python (<code className="text-xs">pip install scholar-sidekick</code>)
                  </p>
                  <CopyableCode
                    ariaLabel="Copy Python example"
                    code={`from scholar_sidekick import ScholarSidekick

client = ScholarSidekick(api_key="${created}")
print(client.format(text="10.1038/nphys1170", style="vancouver").text)`}
                  />
                </div>
                <p className="text-xs text-gray-600">
                  Full setup for every surface: <Link href="/docs">API docs</Link> ·{" "}
                  <Link href="/mcp">MCP server</Link>
                </p>
              </div>
            </div>
          )}

          <form onSubmit={createKey} className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="flex-1 rounded-md border px-3 py-2 text-sm"
              placeholder="Key label (e.g. my-laptop)"
              maxLength={80}
              aria-label="New key label"
            />
            <Button type="submit" disabled={busy}>
              {busy ? "Creating…" : "Create key"}
            </Button>
          </form>

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          {keys.length === 0 ? (
            <p className="text-sm text-gray-500">No keys yet.</p>
          ) : (
            <ul className="divide-y">
              {keys.map((k) => (
                <li key={k.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium">{k.label}</p>
                    <p className="text-xs text-gray-500">
                      {k.masked} · created {CREATED_AT_FORMAT.format(new Date(k.createdAt))}
                    </p>
                  </div>
                  <Button type="button" variant="outline" onClick={() => revoke(k.id)}>
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
